import {
  AdminApiV1QuestionAttemptsPostRequest,
  QuestionAttempt,
  Questionnaire,
} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import {AxiosResponse} from 'axios';
import {AnyAction, Reducer} from 'redux';
import {call, put, StrictEffect} from 'redux-saga/effects';
import {createSelector} from 'reselect';
import {ApplicationState} from 'store/rootReducer';
import {action} from 'typesafe-actions';
import {StoreStatus} from 'utils/types/store';
import {QuestionAttemptExtended} from 'utils/types/questionnaires';
import {QuestionAttemptResponse} from 'APIs/letrusAPI/questionnaire/types';
import {
  fetchPracticeTestAnswersByStepService,
  fetchPracticeTestFeedbackService,
  fetchQuestionAnswersByStudentService,
  fetchQuestionnaireAnswersByStepService,
  fetchQuestionnaireByIdService,
  postPracticeTestAnswerService,
  postQuestionAnswerService,
} from '../../services/questionnaireServices';

export interface QuestionAttemptData {
  option?: number;
  feedback?: string;
}

// Contract re-confirmed live against api/views/question_attempts.py::practice_test_feedback
// (CPAL-SAEB, 2026-09-18 — curl against the running backend). `comparison` only comes on
// Simulado SAEB 2 (Atividade 3), and only when a prior diagnostic exists to compare against —
// absent (not `null`) in every other case. Real shape is flat, not the nested
// `{previous: {...}, difference}` the earlier (stale) contract note assumed.
export interface PracticeTestFeedbackComparison {
  previous_correct_answers: number;
  current_correct_answers: number;
}

export interface PracticeTestFeedbackData {
  score?: number;
  time_spent?: number;
  level?: number | null;
  // `level_label` is the PEDAGOGICAL name of the SAEB band ("Inicial"/"Básico"/"Proficiente"/
  // "Avançado", shared with trail routing in reading_cycle.BAND_TO_LEVEL — must not change) and
  // must NEVER be shown to the student. `level_title` is the real student-facing title
  // ("Iniciante"/"Aprendiz"/"Em Prática"/"Confiante"), null outside the Reading Cycle trails —
  // it's what the front uses to know which scale came back (see resolveSaebFeedbackLevel).
  // Confirmed against the real diff of letrustech/letrus-api#2213
  // (compositions/services/saeb_proficiency.py::READING_CYCLE_LEVELS).
  level_label?: string | null;
  level_title?: string | null;
  // Real correct/total count on the Reading Cycle (absent outside it) — confirmed live
  // 2026-09-18, closes the TODO(fatia3) that used to block this on MOCK_CORRECT_COUNT.
  correct_answers?: number;
  total_questions?: number;
  comparison?: PracticeTestFeedbackComparison | null;
}

// Action types
export enum QuestionnairesTypes {
  FETCH_QUESTIONNAIRE_BY_ID_REQUEST = '@questionnaires/FETCH_QUESTIONNAIRE_BY_ID_REQUEST',
  FETCH_QUESTIONNAIRE_BY_ID_SUCCESS = '@questionnaires/FETCH_QUESTIONNAIRE_BY_ID_SUCCESS',
  FETCH_QUESTIONNAIRE_BY_ID_FAILURE = '@questionnaires/FETCH_QUESTIONNAIRE_BY_ID_FAILURE',

  POST_QUESTION_ANSWER_REQUEST = '@questionnaires/POST_QUESTION_ANSWER_REQUEST',
  POST_QUESTION_ANSWER_SUCCESS = '@questionnaires/POST_QUESTION_ANSWER_SUCCESS',
  POST_QUESTION_ANSWER_FAILURE = '@questionnaires/POST_QUESTION_ANSWER_FAILURE',

  POST_PRACTICE_TEST_ANSWER_REQUEST = '@questionnaires/POST_PRACTICE_TEST_ANSWER_REQUEST',
  POST_PRACTICE_TEST_ANSWER_SUCCESS = '@questionnaires/POST_PRACTICE_TEST_ANSWER_SUCCESS',
  POST_PRACTICE_TEST_ANSWER_FAILURE = '@questionnaires/POST_PRACTICE_TEST_ANSWER_FAILURE',

  FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_REQUEST = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_REQUEST',
  FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_SUCCESS = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_SUCCESS',
  FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_FAILURE = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_FAILURE',

  FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_REQUEST = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_REQUEST',
  FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_SUCCESS = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_SUCCESS',
  FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_FAILURE = '@questionnaires/FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_FAILURE',

  FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_REQUEST = '@questionnaires/FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_REQUEST',
  FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_SUCCESS = '@questionnaires/FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_SUCCESS',
  FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_FAILURE = '@questionnaires/FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_FAILURE',

  FETCH_PRACTICE_TEST_FEEDBACK_REQUEST = '@questionnaires/FETCH_PRACTICE_TEST_FEEDBACK_REQUEST',
  FETCH_PRACTICE_TEST_FEEDBACK_SUCCESS = '@questionnaires/FETCH_PRACTICE_TEST_FEEDBACK_SUCCESS',
  FETCH_PRACTICE_TEST_FEEDBACK_FAILURE = '@questionnaires/FETCH_PRACTICE_TEST_FEEDBACK_FAILURE',

  ClEAR_QUESTION_ATTEMPT = '@newQuestionnaires/ClEAR_QUESTION_ATTEMPT',
}

// State type
export interface QuestionnairesState {
  readonly questionnaire: Questionnaire;
  readonly questionnaireAnswers: QuestionAttemptExtended[];
  readonly status: StoreStatus;
  readonly practiceTestFeedback: PracticeTestFeedbackData | null;
}

// Create actions
export const fetchQuestionnaireByIdRequest = (
  questionnaireId?: string | number,
): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_REQUEST, {
    questionnaireId,
  });

export const fetchQuestionnaireByIdSuccess = (data: Questionnaire): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_SUCCESS, {data});

export const fetchQuestionnaireByIdFailure = (): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_FAILURE);

export const postQuestionAnswerRequest = (
  questionAttemptData: Partial<QuestionAttempt>,
  params?: Record<string, number>,
): AnyAction =>
  action(QuestionnairesTypes.POST_QUESTION_ANSWER_REQUEST, {
    questionAttemptData,
    params,
  });

export const postQuestionAnswerSuccess = (
  data: AdminApiV1QuestionAttemptsPostRequest,
): AnyAction => {
  return action(QuestionnairesTypes.POST_QUESTION_ANSWER_SUCCESS, {
    data,
  });
};

export const postQuestionAnswerFailure = (
  data: AdminApiV1QuestionAttemptsPostRequest,
): AnyAction =>
  action(QuestionnairesTypes.POST_QUESTION_ANSWER_FAILURE, {data});

export const postPracticeTestAnswerRequest = (
  questionAttemptData: Partial<QuestionAttemptResponse>,
  params?: Record<string, number>,
): AnyAction =>
  action(QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_REQUEST, {
    questionAttemptData,
    params,
  });

export const postPracticeTestAnswerSuccess = (
  data: QuestionAttemptExtended,
): AnyAction => {
  return action(QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_SUCCESS, {
    data,
  });
};

export const postPracticeTestAnswerFailure = (
  data: AdminApiV1QuestionAttemptsPostRequest,
): AnyAction =>
  action(QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_FAILURE, {data});

export const fetchQuestionnaireAnswersByStepRequest = (
  stepId: string | number,
  learningPathUserInstanceId?: string | number,
): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_REQUEST, {
    stepId,
    learningPathUserInstanceId,
  });

export const fetchQuestionnaireAnswersByStepSuccess = (
  questionnaireAnswers: QuestionAttempt[],
): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_SUCCESS, {
    questionnaireAnswers,
  });

export const fetchQuestionnaireAnswersByStepFailure = (): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_FAILURE);

export const fetchQuestionnaireAnswersByStudentRequest = (
  studentId: number,
): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_REQUEST, {
    studentId,
  });

export const fetchQuestionnaireAnswersByStudentSuccess = (
  questionnaireAnswers: QuestionAttempt[],
): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_SUCCESS, {
    questionnaireAnswers,
  });

export const fetchQuestionnaireAnswersByStudentFailure = (): AnyAction =>
  action(QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_FAILURE);

export const fetchPracticeTestAnswersByStepRequest = (
  stepId: string | number,
  learningPathUserInstanceId?: string | number,
): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_REQUEST, {
    stepId,
    learningPathUserInstanceId,
  });

export const fetchPracticeTestAnswersByStepSuccess = (
  questionnaireAnswers: QuestionAttemptExtended[],
): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_SUCCESS, {
    questionnaireAnswers,
  });

export const fetchPracticeTestAnswersByStepFailure = (): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_FAILURE);

export const fetchPracticeTestFeedbackRequest = (
  stepId: string | number,
  learningPathUserInstanceId?: string | number,
): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_REQUEST, {
    stepId,
    learningPathUserInstanceId,
  });

export const fetchPracticeTestFeedbackSuccess = (
  questionnaireAnswers: QuestionAttempt[],
): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_SUCCESS, {
    questionnaireAnswers,
  });

export const fetchPracticeTestFeedbackFailure = (): AnyAction =>
  action(QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_FAILURE);

export const clearQuestionnaireAnswers = (): AnyAction =>
  action(QuestionnairesTypes.ClEAR_QUESTION_ATTEMPT);

// Sagas
export function* fetchQuestionnaireById(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<Questionnaire>> {
  try {
    const response = yield call(
      fetchQuestionnaireByIdService,
      action.payload.questionnaireId,
    );

    yield put(fetchQuestionnaireByIdSuccess(response.data));
  } catch (err) {
    yield put(fetchQuestionnaireByIdFailure());
  }
}

export function* postQuestionAnswers(
  action: AnyAction,
): Generator<
  StrictEffect,
  void,
  AxiosResponse<AdminApiV1QuestionAttemptsPostRequest>
> {
  try {
    const response = yield call(
      postQuestionAnswerService,
      action.payload.questionAttemptData,
      action.payload.params,
    );

    yield put(postQuestionAnswerSuccess(response.data));
  } catch (error) {
    const err = error as any;
    yield put(postQuestionAnswerFailure(err?.response?.data));
  }
}

export function* postPracticeTestAnswers(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<QuestionAttemptExtended>> {
  try {
    const response = yield call(
      postPracticeTestAnswerService,
      action.payload.questionAttemptData,
      action.payload.params,
    );

    yield put(postPracticeTestAnswerSuccess(response.data));
  } catch (error) {
    const err = error as any;
    yield put(postPracticeTestAnswerFailure(err?.response?.data));
  }
}

export function* fetchQuestionnaireAnswersByStep(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<QuestionAttempt[]>> {
  try {
    const response = yield call(
      fetchQuestionnaireAnswersByStepService,
      action.payload.stepId,
      action.payload.learningPathUserInstanceId,
    );

    yield put(fetchQuestionnaireAnswersByStepSuccess(response.data));
  } catch (err) {
    yield put(fetchQuestionnaireAnswersByStepFailure());
  }
}

export function* fetchQuestionnaireAnswersByStudent(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<QuestionAttempt[]>> {
  try {
    const response = yield call(
      fetchQuestionAnswersByStudentService,
      action.payload.studentId,
    );

    yield put(fetchQuestionnaireAnswersByStudentSuccess(response.data));
  } catch (err) {
    yield put(fetchQuestionnaireAnswersByStudentFailure());
  }
}

export function* fetchPracticeTestAnswersByStep(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<QuestionAttemptExtended[]>> {
  try {
    const response = yield call(
      fetchPracticeTestAnswersByStepService,
      action.payload.stepId,
      action.payload.learningPathUserInstanceId,
    );

    yield put(fetchPracticeTestAnswersByStepSuccess(response.data));
  } catch (err) {
    yield put(fetchPracticeTestAnswersByStepFailure());
  }
}

export function* fetchPracticeTestFeedback(
  action: AnyAction,
): Generator<StrictEffect, void, AxiosResponse<QuestionAttempt[]>> {
  try {
    const response = yield call(
      fetchPracticeTestFeedbackService,
      action.payload.stepId,
      action.payload.learningPathUserInstanceId,
    );

    yield put(fetchPracticeTestFeedbackSuccess(response.data));
  } catch (err) {
    yield put(fetchPracticeTestFeedbackFailure());
  }
}

// Selectors
const questionnairesDataSelector = (state: ApplicationState) =>
  state.get('questionnaires') as QuestionnairesState;

export const getQuestionnaire = createSelector(
  questionnairesDataSelector,
  (questionnaires) => questionnaires.questionnaire,
);

export const getQuestionnaireAnswers = createSelector(
  questionnairesDataSelector,
  (questionnaires) => questionnaires.questionnaireAnswers,
);

export const getQuestionnaireStatus = createSelector(
  questionnairesDataSelector,
  (questionnaires) => questionnaires.status,
);

export const getPracticeTestFeedback = createSelector(
  questionnairesDataSelector,
  (questionnaires) => questionnaires.practiceTestFeedback,
);

export const getIsPracticeTestFinished = createSelector(
  questionnairesDataSelector,
  (questionnaires) =>
    questionnaires.questionnaireAnswers?.some((answer) => answer?.is_finished),
);

// Initial state
export const INITIAL_STATE: QuestionnairesState = {
  questionnaire: {
    id: -1,
    name: '',
    steps: [],
    created: '',
    modified: '',
  },
  questionnaireAnswers: [],
  status: {
    fulfilled: false,
    loading: false,
    error: false,
    posting: false,
    errorMessage: '',
  },
  practiceTestFeedback: null,
};

// Reducer
export const reducer: Reducer<QuestionnairesState> = (
  state = INITIAL_STATE,
  action,
) => {
  switch (action.type) {
    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_REQUEST:
      return {
        ...state,
        status: {loading: true, error: false, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_SUCCESS:
      return {
        ...state,
        questionnaire: action.payload.data,
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_BY_ID_FAILURE:
      return {
        ...state,
        status: {loading: false, error: true, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.POST_QUESTION_ANSWER_REQUEST:
      return {
        ...state,
        status: {loading: false, error: false, fulfilled: false, posting: true},
      };

    case QuestionnairesTypes.POST_QUESTION_ANSWER_SUCCESS:
      return {
        ...state,
        questionnaireAnswers: [
          ...state.questionnaireAnswers,
          action.payload.data,
        ],
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.POST_QUESTION_ANSWER_FAILURE:
      return {
        ...state,
        status: {
          loading: false,
          error: true,
          fulfilled: false,
          posting: false,
          errorMessage: action.payload.data?.non_field_errors[0],
        },
      };

    case QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_REQUEST:
      return {
        ...state,
        status: {loading: false, error: false, fulfilled: false, posting: true},
      };

    case QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_SUCCESS:
      return {
        ...state,
        questionnaireAnswers: state.questionnaireAnswers
          .filter((answer) => answer.question !== action.payload.data.question)
          .concat(action.payload.data),
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.POST_PRACTICE_TEST_ANSWER_FAILURE:
      return {
        ...state,
        status: {
          loading: false,
          error: true,
          fulfilled: false,
          posting: false,
          errorMessage: action.payload.data?.non_field_errors[0],
        },
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_REQUEST:
      return {
        ...state,
        status: {loading: true, error: false, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_SUCCESS:
      return {
        ...state,
        questionnaireAnswers: action.payload.questionnaireAnswers.results,
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STUDENT_FAILURE:
      return {
        ...state,
        status: {loading: false, error: true, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_REQUEST:
      return {
        ...state,
        status: {loading: true, error: false, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_SUCCESS:
      return {
        ...state,
        questionnaireAnswers: action.payload.questionnaireAnswers.results,
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.FETCH_QUESTIONNAIRE_ANSWERS_BY_STEP_FAILURE:
      return {
        ...state,
        status: {loading: false, error: true, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_REQUEST:
      return {
        ...state,
        status: {loading: true, error: false, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_SUCCESS:
      return {
        ...state,
        questionnaireAnswers: action.payload.questionnaireAnswers.results,
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_ANSWERS_BY_STEP_FAILURE:
      return {
        ...state,
        status: {loading: false, error: true, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_REQUEST:
      return {
        ...state,
        status: {loading: true, error: false, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_SUCCESS:
      return {
        ...state,
        practiceTestFeedback: action.payload.questionnaireAnswers,
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };

    case QuestionnairesTypes.FETCH_PRACTICE_TEST_FEEDBACK_FAILURE:
      return {
        ...state,
        status: {loading: false, error: true, fulfilled: false, posting: false},
      };

    case QuestionnairesTypes.ClEAR_QUESTION_ATTEMPT:
      return {
        ...state,
        questionnaireAnswers: [],
        status: {loading: false, error: false, fulfilled: true, posting: false},
      };
    default:
      return state;
  }
};

export default reducer;
